/**
 * Selection provenance and the symbol table.
 *
 * This is the answer to "why not a spreadsheet". A spreadsheet cannot tell you what
 * feeds a cell or what a change will disturb; the graph already knows both, so
 * showing them costs nothing and is the thing an engineer checking a calculation
 * actually wants.
 */
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'

import { formatNumber } from '@/lib/format'
import type { SheetGraph } from '../model/graph'
import type { CellId, CellResult, SheetDoc } from '../model/types'

function cellNumber(doc: SheetDoc, id: CellId): number {
  return doc.cells.findIndex((cell) => cell.id === id) + 1
}

function SymbolRow({
  symbol,
  result,
  index,
}: {
  symbol: string
  result: CellResult | undefined
  index: number
}) {
  const { t } = useTranslation('sheets')
  const value = result?.value
  return (
    <div className="flex items-baseline gap-2 py-1 text-sm">
      {/* Symbols are notation, never translated (14.4). */}
      <span className="min-w-0 flex-1 truncate font-mono text-[13px]">{symbol}</span>
      <span className="tabular text-text-muted">
        {value ? formatNumber(value.value, { decimals: 3 }) : '—'}
      </span>
      <span className="w-12 text-end text-xs text-text-muted">{value?.unit ?? ''}</span>
      <span className="w-10 text-end font-mono text-[11px] text-text-muted">
        {t('sheets.panel.cell_number', { n: index })}
      </span>
    </div>
  )
}

export function VariablesPanel({
  doc,
  graph,
  results,
  selected,
}: {
  doc: SheetDoc
  graph: SheetGraph
  results: ReadonlyMap<CellId, CellResult>
  selected: CellId | null
}) {
  const { t } = useTranslation('sheets')

  const analysis = selected ? graph.analysis.get(selected) : undefined
  const dependsOn = selected ? (graph.dependsOn.get(selected) ?? []) : []
  const dependents = selected ? (graph.dependents.get(selected) ?? []) : []

  const symbolOf = (id: CellId): string =>
    graph.analysis.get(id)?.defines[0] ??
    `${t('sheets.panel.cell_number', { n: cellNumber(doc, id) })}`

  return (
    <div className="flex h-full min-h-0 flex-col">
      <section className="border-b border-border p-4">
        <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {t('sheets.panel.selection')}
        </h2>
        {analysis ? (
          <div className="flex items-baseline justify-between gap-2">
            <span className="font-mono text-base">{analysis.defines[0] ?? '—'}</span>
            <span className="font-mono text-[11px] text-text-muted">
              {t('sheets.panel.cell_number', { n: analysis.index + 1 })}
            </span>
          </div>
        ) : (
          <p className="text-sm text-text-muted">{t('sheets.panel.no_dependencies')}</p>
        )}
      </section>

      <section className="border-b border-border p-4">
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {t('sheets.panel.depends_on')}
        </h3>
        {dependsOn.length === 0 ? (
          <p className="text-sm text-text-muted">{t('sheets.panel.no_dependencies')}</p>
        ) : (
          dependsOn.map((id) => (
            <SymbolRow
              key={id}
              symbol={symbolOf(id)}
              result={results.get(id)}
              index={cellNumber(doc, id)}
            />
          ))
        )}

        <h3 className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
          {t('sheets.panel.used_by')}
        </h3>
        {dependents.length === 0 ? (
          <p className="text-sm text-text-muted">{t('sheets.panel.no_dependents')}</p>
        ) : (
          dependents.map((id) => (
            <SymbolRow
              key={id}
              symbol={symbolOf(id)}
              result={results.get(id)}
              index={cellNumber(doc, id)}
            />
          ))
        )}
      </section>

      <section className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mb-2 flex items-baseline justify-between">
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
            {t('sheets.panel.variables')}
          </h3>
          <span className="font-mono text-[11px] text-text-muted">{graph.finalScope.size}</span>
        </div>
        {[...graph.finalScope.entries()].map(([symbol, id]) => (
          <SymbolRow
            key={symbol}
            symbol={symbol}
            result={results.get(id)}
            index={cellNumber(doc, id)}
          />
        ))}
      </section>

      <footer className="flex items-start gap-2 border-t border-border p-3 text-text-muted">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <p className="text-xs leading-relaxed">{t('sheets.panel.local_note')}</p>
      </footer>
    </div>
  )
}
